import type { ComponentPropsWithoutRef, Dispatch, FocusEvent, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { Input, Stack } from '@etg/wings';
import { MaskedInput } from '@eti/components';
import { useProperty, useSnowplowTracking, useTranslation, useValidation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { constants, persistInputData } from '@eti/utils';
import type {
  EventWithDataHandler,
  WrappedFieldInputProps,
  WrappedFieldMetaProps,
} from 'redux-form';
import { Field as ReduxFormField } from 'redux-form';
import { agencyFields, contactFields, fieldRestrictions } from '../../constants/formFields';
import type { Country } from '../../models';
import PhoneCountryCodeInput from './PhoneCountryCodeInput';

const { MOUSE_FLOW_CLASS_NAMES } = constants;
const { AGENT_PHONE } = agencyFields;
const { EMERGENCY_PHONE, PHONE } = contactFields;

const numericFormat = /^[0-9]*$/;
export const isNumber = (x: string) => numericFormat.test(x);

const inputStyles = css`
  width: 100%;
`;

export const numberMask = (value = '') =>
  Array.from(value)
    .filter(isNumber)
    .map(() => /\d/);

interface MaskedInputComponentProps {
  hasAsyncError: boolean;
  input: WrappedFieldInputProps;
  meta: WrappedFieldMetaProps;
  name: string;
  onValidationError: (error: string | null) => void;
  placeholder: string;
  [key: string]: any; // escape-hatch for ...props
}

const MaskedInputComponent = ({
  hasAsyncError,
  input,
  meta,
  onValidationError,
  placeholder,
  ...props
}: MaskedInputComponentProps) => {
  const hasError =
    hasAsyncError || Boolean(meta.invalid && !meta.active && (input.value !== '' || meta.touched));

  useEffect(() => {
    if (hasError) {
      onValidationError(meta.error);
    } else {
      onValidationError(null);
    }
  }, [meta.error, hasError, onValidationError]);

  return (
    <MaskedInput
      autoComplete="tel-national"
      autoCorrect="off"
      className={cx(inputStyles, MOUSE_FLOW_CLASS_NAMES)}
      hasError={hasError}
      inputMode="tel"
      mask={numberMask}
      maxLength={fieldRestrictions.MAX_CHARS}
      placeholder={placeholder}
      spellCheck="false"
      type="tel"
      {...input}
      {...props}
    />
  );
};

interface ConnectedPhoneInputProps extends ComponentPropsWithoutRef<'input'> {
  descriptionId?: string;
  form: string;
  hasAsyncError?: boolean;
  name: string;
  onBlur?: EventWithDataHandler<FocusEvent<HTMLInputElement>>;
  onValidationError: Dispatch<SetStateAction<undefined>>;
  [key: string]: any; // escape-hatch
}

const ConnectedPhoneInput = ({ form, hasAsyncError, name, ...props }: ConnectedPhoneInputProps) => {
  const { p } = useProperty();
  const validate = useValidation(name);
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const [hasPhoneNumberChanged, setHasPhoneNumberChanged] = useState(false);

  const handleOnChange = () => {
    if (
      isSnowplowTrackingEnabled &&
      !hasPhoneNumberChanged &&
      (name === PHONE || name === EMERGENCY_PHONE)
    ) {
      const labelName = name === PHONE ? 'Phone-number' : 'Emergency-phone-number';
      sendTrackEvent({
        category: 'Traveler Details',
        action: 'Input',
        label: labelName,
      });
      setHasPhoneNumberChanged(true);
    }
  };

  const handleOnBlur = p('IbeClient.TravelerDetails.PersistFormData.Enabled')
    ? (event?: FocusEvent<HTMLInputElement>) => persistInputData(form, name)(event?.target.value)
    : undefined;

  return (
    <ReduxFormField
      component={MaskedInputComponent as any}
      name={name}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      props={{ name, hasAsyncError, ...props } as any}
      validate={validate}
    />
  );
};

export type PhoneType = typeof AGENT_PHONE | typeof PHONE | typeof EMERGENCY_PHONE;

interface PhoneProps {
  countries: Country[];
  countryCodeLabel: string;
  'data-testid'?: string;
  description?: string;
  form: string;
  formSection: string;
  hasAsyncError?: boolean;
  id: string;
  label: string;
  name: PhoneType;
  onCountryChange?: (phoneType: PhoneType) => void;
  phoneCountryId: string;
  placeholder?: string;
  shouldMarkInputAsRequired?: boolean;
}

const Phone = ({
  countries = [],
  countryCodeLabel,
  'data-testid': dataTestid,
  description,
  form,
  formSection,
  hasAsyncError,
  id,
  label,
  name,
  onCountryChange,
  phoneCountryId,
  placeholder,
  shouldMarkInputAsRequired,
}: PhoneProps) => {
  const { nonDebugT } = useTranslation();
  const [validationError, setValidationError] = useState(undefined);

  const descriptionId = description ? `${name}-description` : undefined;
  const resolvedLabel = shouldMarkInputAsRequired
    ? nonDebugT('Input.Field.Required', label)
    : label;
  const errorId = `${id}-error`;
  const labelId = `${id}-label`;

  return (
    <Stack spacing={4}>
      <Input.Label htmlFor={id} id={labelId}>
        {resolvedLabel}
      </Input.Label>
      <Input.Group style={{ position: 'relative' }}>
        <PhoneCountryCodeInput
          associatedPhoneInput={name}
          countries={countries}
          form={form}
          formSection={formSection}
          label={countryCodeLabel}
          name={phoneCountryId}
          onCountryChange={onCountryChange}
        />
        <ConnectedPhoneInput
          aria-labelledby={`${labelId} ${errorId}`}
          autoComplete="tel-national"
          autoCorrect="off"
          data-testid={dataTestid}
          descriptionId={descriptionId}
          form={form}
          hasAsyncError={hasAsyncError}
          id={id}
          inputMode="tel"
          maxLength={fieldRestrictions.MAX_CHARS}
          name={name}
          onValidationError={setValidationError}
          placeholder={placeholder}
          required={Boolean(shouldMarkInputAsRequired)}
          spellCheck="false"
          type="tel"
        />
      </Input.Group>
      <Input.Meta
        description={description}
        descriptionId={descriptionId}
        error={validationError}
        errorId={errorId}
        hasError={Boolean(validationError)}
      />
    </Stack>
  );
};

export default Phone;
